import type { CookieInterface, CookieSetOptions } from '@app/services/CookieManager'

import { tryOr } from '@app/utils/tryOr'

type TypedCookie<T> = {
  get: (context: CookieInterface) => Promise<T>
  set: (context: CookieInterface, value: T, ignoreConsent?: boolean) => Promise<void>
  rm: (context: CookieInterface) => Promise<void>
}

/**
 * Defines a strongly-typed cookie with custom (un)marshalling. The value is
 * URI-encoded on write and decoded on read. All three operations are async — a
 * read awaits the underlying {@link CookieInterface}, and write/remove return the
 * underlying Promise so ordering-sensitive callers can await it.
 */
export const createCookie = <T = string>(
  name: string,
  unmarshall: (input: string) => T,
  marshall: (input: T) => string,
  options?: () => CookieSetOptions
): TypedCookie<T> => ({
  get: async (context: CookieInterface): Promise<T> => {
    const raw = await context.get(name)
    return unmarshall(tryOr(() => decodeURIComponent(raw ?? ''), ''))
  },
  set: (context: CookieInterface, value: T, ignoreConsent?: boolean): Promise<void> => {
    let opts = options?.()
    if (ignoreConsent) opts = { ...opts, ignoreConsent: true }
    return context.set(name, encodeURIComponent(marshall(value)), opts)
  },
  rm: (context: CookieInterface): Promise<void> => context.remove(name),
})
